import { ChangeDetectionStrategy, Component, Input, OnInit, inject, signal } from '@angular/core';
import { AssetDto, FolderDto, GalleryDto } from '@nusign/picflow-backend-types';
import { QrCodeDialogComponent } from '../../../qr-code/dialogs/qr-code-dialog/qr-code-dialog.component';
import { DialogService } from '../../../services/dialog.service';
import { ShareService } from '../../../services/share.service';
import { StateService } from '../../../services/state.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';

@Component({
  selector: 'app-share-gallery-social-links',
  templateUrl: './share-gallery-social-links.component.html',
  styleUrls: ['./share-gallery-social-links.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective, TooltipDirective, SvgComponent],
})
export class ShareGallerySocialLinksComponent implements OnInit {
  private shareService = inject(ShareService);
  private state = inject(StateService);
  private dialog = inject(DialogService);
  @Input() gallery: GalleryDto;
  @Input() folder: FolderDto;
  @Input() asset?: AssetDto;
  @Input() disabled: boolean;

  publicLink = signal<string>(undefined);

  public emailHref = signal<string>(undefined);
  public facebookHref = signal<string>(undefined);
  public twitterHref = signal<string>(undefined);
  public linkedinHref = signal<string>(undefined);
  public whatsappHref = signal<string>(undefined);

  ngOnInit(): void {
    this.getLink();
  }

  private async getCorrectLink() {
    if (this.folder) {
      return this.shareService.getFolderShareLink(this.folder);
    }
    return this.shareService.getShareLink(this.gallery, { assetId: this.asset?.id });
  }

  private getTitle() {
    if (this.folder) return `Folder: ${this.folder.title || 'Folder Title'}`;
    return `Gallery: ${this.gallery.title || 'Untitled'}`;
  }

  private async getLink() {
    const link = await this.getCorrectLink();
    this.publicLink.set(link.url);

    const title = this.getTitle();
    const emailText = this.getEmailText();
    const whatsappText = this.getWhatsappText();
    const twitterText = this.getTwitterText();

    this.emailHref.set(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(emailText)}`);
    this.facebookHref.set(`https://www.facebook.com/sharer/sharer.php?u=${this.publicLink()}`);
    this.twitterHref.set(`https://twitter.com/intent/tweet?url=${this.publicLink()}&text=${twitterText}`);
    this.linkedinHref.set(`https://www.linkedin.com/shareArticle?mini=true&url=${this.publicLink()}`);
    // this.slackHref = /
    this.whatsappHref.set(`https://api.whatsapp.com/send?text=${encodeURIComponent(whatsappText)}`);
  }

  private getEmailText() {
    const activeName = this.folder ? 'folder' : 'gallery';
    if (this.gallery?.password_enabled) {
      return `Hello,\n\nHere's the link to the ${activeName}: ${this.publicLink()}\n\nThe password is: ${
        this.gallery['password']
      }\n\nBest regards,\n${this.state.user?.first_name || ''}\n`;
    } else {
      return `Hello,\n\nHere's the link to the ${activeName}: ${this.publicLink()}\n\nBest regards,\n${
        this.state.user?.first_name || ''
      }\n`;
    }
  }

  private getWhatsappText() {
    const activeName = this.folder ? 'folder' : 'gallery';
    if (this.gallery?.password_enabled) {
      return `Hello, Here's the link to the ${activeName}: ${this.publicLink()}. The password is: ${this.gallery['password']}`;
    } else {
      return `Hello, Here's the link to the ${activeName}: ${this.publicLink()}`;
    }
  }

  private getTwitterText() {
    const activeName = this.folder ? 'folder' : 'gallery';
    if (this.gallery?.password_enabled) {
      return `Hello, Here's the link to the ${activeName}. The password is: ${this.gallery['password']}`;
    } else {
      return `Hello, Here's the link to the ${activeName}`;
    }
  }

  openQrCodeDialog() {
    this.dialog.open(QrCodeDialogComponent, { content: this.publicLink() });
  }
}
